import * as React from 'react'
import styled from 'styled-components'

import * as api from '@owl-nest/api-client/latest'
import { t } from '@owl-nest/localize'
import * as hooks from '@owl-nest/hooks'
import * as models from '@owl-nest/models'
import * as plume from '@ulule/owl-kit-components/next'
import { CATEGORY, EVENT_TYPE, SITE, useTracking } from '@owl-nest/shadow'

import { useDisplayMode } from '../hooks/useDisplayMode'
import * as UFE from '../UFE'
import { getCurrentLocale } from '../router'

export type ProjectBannerProps = {
  className?: string
  project: models.project.ProjectForOwner
  owner: api.Project['owner']
  latestProject: api.Project
}

function ProjectBannerComponent({ className, project, owner, latestProject }: ProjectBannerProps): React.ReactElement {
  const displayMode = useDisplayMode(project)
  const isEditMode = displayMode === 'edit'
  const router = hooks.useNextRouter()
  const currentLocale = getCurrentLocale(router)

  const tracking = useTracking()

  const date = latestProject.date_end ? new Date(latestProject.date_end) : undefined
  const currentProjectTitle = models.project.name(project, currentLocale)
  const latestProjectTitle = models.project.name(latestProject, currentLocale)

  return (
    <ProjectBannerWrapper className={className}>
      {isEditMode && (
        <a href={`/projects/${project.id}/backoffice/#ongoing-fundraising`}>
          <plume.EditButton glyph={<plume.glyphs.stroke.Edit size={18} />}>{t('Edit')}</plume.EditButton>
        </a>
      )}
      <ProjectBannerHeader>
        <plume.illustrations.twoToned.LoudSpeaker size={81} inverted />
        <ProjectBannerTitle asDesktop="L">
          {t('%(owner)s has a project currently funding', {
            owner: models.user.fullName(owner),
          })}
        </ProjectBannerTitle>
      </ProjectBannerHeader>
      <ProjectBannerCard>
        <CTARedirection href={`/${latestProject.slug}/`} />
        <ProjectBannerCardImage
          alt={latestProjectTitle}
          src={models.project.mainImage(latestProject, 'large', currentLocale)}
        />
        <ProjectBannerCardContent>
          <plume.StatusTag color="green">
            <plume.glyphs.stroke.Clock size={12} />
            {models.project.isExtraTime(latestProject)
              ? t('now in extension!')
              : date &&
                t('until %(monthAndDay)s!', {
                  monthAndDay: t('%(month)s %(day)s', {
                    day: date.getDate(),
                    month: date.toLocaleString(UFE.USER_LOCALE, {
                      month: 'long',
                    }),
                  }),
                })}
          </plume.StatusTag>
          <ProjectBannerCardTitle>{latestProjectTitle}</ProjectBannerCardTitle>
          <ProjectBannerCardCTA>
            <plume.Link darkened tinted onClick={trackAndRedirect}>
              {t('See project')}
            </plume.Link>
            <plume.glyphs.stroke.ArrowRight size={18} />
          </ProjectBannerCardCTA>
        </ProjectBannerCardContent>
      </ProjectBannerCard>
    </ProjectBannerWrapper>
  )

  function trackAndRedirect(): void {
    tracking.track({
      event: EVENT_TYPE.GENERIC,
      eventCategory: CATEGORY.MEMBERSHIP_PAGE,
      eventAction: 'project_promotion',
      siteCategory: SITE.ULULE,
      pageCategory: CATEGORY.MEMBERSHIP_PAGE,
      eventCallback: () => {
        if (typeof window !== 'undefined') {
          window.location.href = `/${latestProject.slug}/`
        }
      },
    })
  }
}

export const ProjectBanner = styled(ProjectBannerComponent)``

export const ProjectBannerWrapper = styled.div`
  background-color: ${plume.COLORS.PRIMARY_GREY_000};
  // TODO: Use svg (or external raster) instead?
  background-image: url('data:image/png;base64,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');
  align-items: center;
  box-shadow: ${plume.EFFECTS.SHADOW_5};
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 24px;
  justify-content: center;
  overflow: hidden;
  padding-bottom: 32px;
  padding-inline: 24px;
  padding-top: 32px;
  text-decoration: none;

  ${plume.EditButton} {
    position: absolute;
    right: 16px;
    top: calc(${plume.SIZES.HEADER_HEIGHT} + 54px + 16px);
    z-index: 1;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    flex-direction: row;
    max-height: fit-content;

    ${plume.EditButton} {
      top: calc(${plume.SIZES.HEADER_HEIGHT} + 16px);
    }
  }
`

export const ProjectBannerHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    flex-direction: row;
    max-width: 472px;
    gap: 16px;
  }
`

export const ProjectBannerTitle = styled(plume.styles.heading.S)`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  cursor: default;
  display: inline-block;
  display: -webkit-box;
  height: calc(31px * 3 + 4px);
  max-width: 100%;
  overflow: hidden;
  position: relative;
  text-align: center;
  text-overflow: ellipsis;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    text-align: left;
  }
`

export const ProjectBannerCard = styled.div`
  background-color: ${plume.COLORS.PRIMARY_GREY_000};
  border-radius: 4px;
  box-shadow: ${plume.EFFECTS.SHADOW_3};
  display: flex;
  flex-direction: column;
  max-width: 547px;
  overflow: hidden;
  position: relative;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    flex-direction: row;
    min-width: fit-content;
    padding: 10px 14px;
    height: 146px;
    gap: 20px;
  }
`

export const ProjectBannerCardContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 10px 10px 16px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    align-items: flex-start;
    padding: 0;
  }

  ${plume.StatusTag} {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  ${plume.glyphs.Icon} {
    top: 0 !important;
  }
`

export const ProjectBannerCardImage = styled.img`
  width: 100%;
  min-height: 122px;
  aspect-ratio: 16/9;
  object-fit: cover;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    max-width: 219px;
  }
`

export const ProjectBannerCardTitle = styled(plume.styles.heading.XXXS)`
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  position: relative;
  padding: 8px 0;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    text-align: left;
    max-width: 277px;
    padding: 4px 0;
  }
`

export const ProjectBannerCardCTA = styled(plume.styles.copy.S)`
  align-items: center;
  color: ${plume.COLORS.PRIMARY_BLUE_700};
  display: flex;
  font-weight: 600; // HACK: Irregular font manipulation.
  gap: 8px;
  margin-top: auto;

  ${plume.glyphs.stroke.ArrowRight} {
    margin-top: 1px;
  }

  ${plume.Link} {
    color: ${plume.COLORS.PRIMARY_BLUE_700} !important;
  }
`

const CTARedirection = styled.a`
  display: block;
  height: 100%;
  position: absolute;
  width: 100%;
`
